/**
 * SavedFilterDropdown — Cloudscape "Saved filter sets" pattern.
 *
 * Two UI elements:
 * 1. A Select dropdown (labeled "Saved filter sets") for switching
 *    between saved/predefined filter sets.
 * 2. A ButtonDropdown (attached to "Clear filters") with actions:
 *    Save as new / Update current / Delete current / Set as default.
 *
 * See: https://cloudscape.design/patterns/general/filter-patterns/saved-filter-sets/
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  FormField,
  Input,
  Modal,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";

import { RootState } from "../app/rootReducer";
import {
  PropertyFilterGroup,
  hasAnyFilter,
  setQueryMode,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
  setQueryString,
  sumPropertyFilterQueries,
} from "../searchSideBar/searchSideBarConnector";
import { PREDEFINED_FILTERS } from "./predefinedFilters";
import { setAsinPageNumber } from "../porAsinPageControl/porAsinPageControl";
import { setValidationPageNumber } from "../validation/asinValidationTableConnector";
import { setBaselinePageNumber } from "../dateBaseline/baselineTableConnector";
import { trackButtonClick } from "../telemetry/telemetry";
import {
  createSavedFilter,
  deleteSavedFilter,
  fetchSavedFilters,
  updateSavedFilter,
  SavedFilter,
  setActiveFilter,
  setActivePredefined,
  clearActiveFilter,
} from "./savedFiltersSlice";

// ---- Saved Filter Sets Select ----

export const SavedFilterSetsSelect: React.FC = () => {
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.savedFilters.filters);
  const status = useSelector((state: RootState) => state.savedFilters.status);
  const activeSelectionId = useSelector(
    (state: RootState) => state.savedFilters.activeSelectionId
  );
  const queryPropertyFilters = useSelector(
    (state: RootState) => state.generateQuery.queryPropertyFilters
  );

  // Fetch saved filters on mount.
  useEffect(() => {
    dispatch(fetchSavedFilters());
  }, [dispatch]);

  // Drop the active saved / predefined selection once the user has
  // deleted every chip. The dropdown label would otherwise keep
  // showing the last-applied filter name over an empty state.
  useEffect(() => {
    if (activeSelectionId && !hasAnyFilter(queryPropertyFilters)) {
      dispatch(clearActiveFilter());
    }
  }, [activeSelectionId, queryPropertyFilters, dispatch]);

  const options = useMemo<SelectProps.OptionGroup[]>(() => {
    const groups: SelectProps.OptionGroup[] = [];

    if (PREDEFINED_FILTERS.length > 0) {
      groups.push({
        label: "Quick Filters",
        options: PREDEFINED_FILTERS.map((pf) => ({
          value: `predefined:${pf.id}`,
          label: pf.label,
          iconName: "calendar" as const,
        })),
      });
    }

    if (filters.length > 0) {
      groups.push({
        label: "My Saved Filters",
        options: filters.map((sf) => ({
          value: `saved:${sf.docId}`,
          label: sf.name,
          iconName: "file" as const,
        })),
      });
    }

    return groups;
  }, [filters]);

  // Derive selectedOption from Redux state — single source of truth.
  // For predefined "Quick Filters", drop the shown name once the
  // current chips diverge from what the predefined filter produces:
  // the label would be misleading. User-saved filters keep their
  // name so "Update current filter set" remains reachable.
  const selectedOption = useMemo<SelectProps.Option | null>(() => {
    if (!activeSelectionId) return null;
    if (activeSelectionId.startsWith("predefined:")) {
      const predefId = activeSelectionId.slice("predefined:".length);
      const predef = PREDEFINED_FILTERS.find((p) => p.id === predefId);
      if (predef) {
        try {
          const currentQS =
            sumPropertyFilterQueries(queryPropertyFilters).toString();
          const expectedQS = sumPropertyFilterQueries(
            predef.buildFilters()
          ).toString();
          if (currentQS !== expectedQS) return null;
        } catch {
          return null;
        }
      }
    }
    for (const group of options) {
      const match = group.options?.find((o) => o.value === activeSelectionId);
      if (match) return match;
    }
    return null;
  }, [activeSelectionId, options, queryPropertyFilters]);

  const applyFilter = useCallback(
    (filterGroup: PropertyFilterGroup, label: string) => {
      trackButtonClick("savedFilterSets", "Apply", { filterName: label });
      dispatch(setQueryMode("PropertyFilter"));
      dispatch(setQueryPropertyFilters(filterGroup));
      dispatch(setQueryPropertyFiltersForceRehydrate(true));
      dispatch(setQueryString());
      dispatch(setAsinPageNumber({ pageNumber: 1 }));
      dispatch(setValidationPageNumber({ pageNumber: 1 }));
      dispatch(setBaselinePageNumber({ pageNumber: 1 }));
    },
    [dispatch]
  );

  const handleChange = useCallback(
    ({ detail }: { detail: SelectProps.ChangeDetail }) => {
      const id = detail.selectedOption.value ?? "";

      if (id.startsWith("predefined:")) {
        const predefId = id.replace("predefined:", "");
        const predef = PREDEFINED_FILTERS.find((p) => p.id === predefId);
        if (predef) {
          applyFilter(predef.buildFilters(), predef.label);
          dispatch(setActivePredefined(predefId));
        }
      } else if (id.startsWith("saved:")) {
        const docId = id.replace("saved:", "");
        const saved = filters.find((f) => f.docId === docId);
        if (saved) {
          applyFilter(saved.propertyFilters, saved.name);
          dispatch(setActiveFilter(docId));
        }
      }
    },
    [applyFilter, filters, dispatch]
  );

  return (
    <FormField label="Saved filter sets">
      <Select
        data-testid="saved-filter-sets-select"
        selectedOption={selectedOption}
        onChange={handleChange}
        options={options}
        placeholder="Choose a filter set"
        loadingText="Loading..."
        statusType={status === "loading" ? "loading" : "finished"}
        empty="No saved filters"
      />
    </FormField>
  );
};

// ---- Filter Actions Dropdown (attached to Clear filters area) ----

export interface FilterActionsDropdownProps {
  /** Whether there are filters that can be saved. */
  canSave?: boolean;
  /** Called when "Clear filters" main button is clicked. */
  onClear: () => void;
  /** Whether clear is disabled (no filters applied). */
  clearDisabled?: boolean;
}

export const FilterActionsDropdown: React.FC<FilterActionsDropdownProps> = ({
  canSave,
  onClear,
  clearDisabled,
}) => {
  const dispatch = useDispatch();
  const queryPropertyFilters = useSelector(
    (state: RootState) => state.generateQuery.queryPropertyFilters
  );
  const filters = useSelector((state: RootState) => state.savedFilters.filters);
  const activeDocId = useSelector(
    (state: RootState) => state.savedFilters.activeDocId
  );

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveModalName, setSaveModalName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    docId: string;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const activeFilter = activeDocId
    ? filters.find((f) => f.docId === activeDocId) ?? null
    : null;

  // Determine if current filters differ from the active saved filter.
  const isModified = useMemo(() => {
    if (!activeFilter) return false;
    const currentQS = sumPropertyFilterQueries(queryPropertyFilters).toString();
    return currentQS !== activeFilter.queryString;
  }, [activeFilter, queryPropertyFilters]);

  const items: ButtonDropdownProps.Items = [
    {
      id: "save-new",
      text: "Save as new filter set",
      disabled: !canSave,
    },
    {
      id: "update-current",
      text: "Update current filter set",
      disabled: !activeFilter || !isModified,
    },
    {
      id: "delete-current",
      text: "Delete current filter set",
      disabled: !activeFilter,
    },
  ];

  const handleClear = useCallback(() => {
    onClear();
    dispatch(
      setQueryPropertyFilters({
        type: "PropertyFilterGroup",
        filters: [],
        operation: "and",
      })
    );
    dispatch(setQueryPropertyFiltersForceRehydrate(true));
    dispatch(clearActiveFilter());
  }, [onClear, dispatch]);

  const handleItemClick = useCallback(
    ({ detail }: { detail: ButtonDropdownProps.ItemClickDetails }) => {
      if (detail.id === "save-new") {
        setSaveModalOpen(true);
      } else if (detail.id === "update-current") {
        if (activeFilter) {
          const currentQueryString =
            sumPropertyFilterQueries(queryPropertyFilters).toString();
          dispatch(
            updateSavedFilter({
              docId: activeFilter.docId,
              name: activeFilter.name,
              propertyFilters: queryPropertyFilters,
              queryString: currentQueryString,
              bodyVersion: activeFilter.bodyVersion,
            })
          );
        }
      } else if (detail.id === "delete-current") {
        if (activeFilter) {
          setDeleteTarget({
            docId: activeFilter.docId,
            name: activeFilter.name,
          });
        }
      }
    },
    [activeFilter, dispatch, queryPropertyFilters]
  );

  // ---- Save handler ----
  const handleSave = useCallback(async () => {
    if (!saveModalName.trim()) return;
    setSaving(true);
    try {
      const currentQueryString =
        sumPropertyFilterQueries(queryPropertyFilters).toString();
      const created = await (
        dispatch(
          createSavedFilter({
            name: saveModalName.trim(),
            propertyFilters: queryPropertyFilters,
            queryString: currentQueryString,
          })
        ) as any
      ).unwrap();
      dispatch(setActiveFilter(created.docId));
      setSaveModalOpen(false);
      setSaveModalName("");
    } catch {
      // Leave modal open on failure.
    } finally {
      setSaving(false);
    }
  }, [dispatch, saveModalName, queryPropertyFilters]);

  // ---- Delete handler ----
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await (dispatch(deleteSavedFilter(deleteTarget.docId)) as any).unwrap();
      setDeleteTarget(null);
    } catch {
      // Leave modal open on failure.
    } finally {
      setDeleting(false);
    }
  }, [dispatch, deleteTarget]);

  return (
    <>
      <ButtonDropdown
        data-testid="filter-actions-dropdown"
        variant="normal"
        items={items}
        onItemClick={handleItemClick}
        ariaLabel="Filter set actions"
        mainAction={{
          text: "Clear filters",
          onClick: handleClear,
          disabled: clearDisabled,
        }}
      />

      {/* Save Modal */}
      <Modal
        data-testid="save-filter-modal"
        visible={saveModalOpen}
        onDismiss={() => {
          setSaveModalOpen(false);
          setSaveModalName("");
        }}
        header="Save as new filter set"
        closeAriaLabel="Close save filter modal"
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                variant="link"
                disabled={saving}
                onClick={() => {
                  setSaveModalOpen(false);
                  setSaveModalName("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!saveModalName.trim() || saving}
                loading={saving}
                onClick={handleSave}
              >
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField label="Filter set name">
          <Input
            data-testid="save-filter-name-input"
            value={saveModalName}
            onChange={({ detail }) => setSaveModalName(detail.value)}
            placeholder="e.g., Echo US Launch ASINs"
            autoFocus
          />
        </FormField>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        data-testid="delete-filter-modal"
        visible={deleteTarget !== null}
        onDismiss={() => setDeleteTarget(null)}
        header="Delete filter set"
        closeAriaLabel="Close delete confirmation"
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                variant="link"
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={deleting}
                disabled={deleting}
                onClick={handleConfirmDelete}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This
        action cannot be undone.
      </Modal>
    </>
  );
};

export default SavedFilterSetsSelect;
